import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import { getMyArticles, deleteArticle } from '../../services/blogApi';
import './MyArticles.css';

export default function MyArticles() {
  const { user } = useAuth();
  const isPremium = user?.roles?.includes('ROLE_PREMIUM');
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isPremium) {
      setError('Premium membership required');
      setLoading(false);
      return;
    }

    const loadArticles = async () => {
      try {
        const response = await getMyArticles();
        setArticles(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [isPremium]);

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await deleteArticle(articleId);
      setArticles(articles.filter(a => a.id !== articleId));
    } catch (err) {
      alert('Failed to delete article: ' + (err.response?.data?.error || err.message));
    }
  };

  if (!isPremium) {
    return (
      <div className="my-articles-container">
        <BackButton />
        <div className="premium-required">
          <h2>⭐ Premium Membership Required</h2>
          <p>You need a premium membership to create and manage articles.</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="my-articles-container">
        <BackButton />
        <div className="loading">Loading articles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-articles-container">
        <BackButton />
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="my-articles-container">
      <BackButton />
      <div className="articles-header">
        <h1>My Articles</h1>
        <button onClick={() => navigate('/blog/create')} className="create-button">
          + Create New Article
        </button>
      </div>

      {articles.length === 0 ? (
        <div className="no-articles">
          <p>You haven't created any articles yet.</p>
          <button onClick={() => navigate('/blog/create')} className="create-button">
            Create Your First Article
          </button>
        </div>
      ) : (
        <div className="articles-list">
          {articles.map(article => (
            <div key={article.id} className="article-card">
              <div className="article-info">
                <h3>{article.title}</h3>
                {article.description && <p className="article-desc">{article.description}</p>}
                <div className="article-meta">
                  <span className={`status ${article.published ? 'published' : 'draft'}`}>
                    {article.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="date">
                    {new Date(article.createdAt).toLocaleDateString()}
                  </span>
                  {article.published && article.viewCount > 0 && (
                    <span className="views">{article.viewCount} views</span>
                  )}
                </div>
              </div>
              <div className="article-actions">
                {article.published && (
                  <button
                    onClick={() => navigate(`/blog/${article.slug}`)}
                    className="view-button"
                  >
                    View
                  </button>
                )}
                <button
                  onClick={() => navigate(`/blog/edit/${article.id}`)}
                  className="edit-button"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(article.id)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

